import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import buildBlobUrl from '../utils/buildBlobUrl';
import { getToken } from '../utils/auth';
import './AdmitCardPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const DEFAULT_SIG_LAYOUT = { x: 0, y: 0, h: 44 };

const SIG_ROLES = ['classTeacher', 'examIC', 'principal'];
const SIG_LABELS = { classTeacher: 'Class Teacher', examIC: 'Exam IC', principal: 'Principal' };

// Inline style so the position carries into the print window's copied HTML
const sigImgStyle = (sig) => {
  const l = { ...DEFAULT_SIG_LAYOUT, ...(sig?.layout || {}) };
  return { height: `${l.h}px`, transform: `translate(${l.x}px, ${l.y}px)` };
};

/**
 * Single half-page Admit Card.
 * Header (logo + school name/address) has NO colored background — plain.
 * Below: exam name, then student fields, then Class Teacher & Principal signatures.
 */
const AdmitCard = ({ school, examName, entry, sigs }) => {
  const s = entry || {};
  return (
    <div className="admit-card">
      <div className="admit-card-inner">
        {/* Plain header — no colored background */}
        <div className="admit-header">
          {school.schoolLogo && <img src={school.schoolLogo} alt="" className="admit-logo" />}
          <div className="admit-header-text">
            <h1 className="admit-school-name">{school.schoolName || 'School Name'}</h1>
            {school.schoolAddress && (
              <p
                className="admit-school-addr"
                dangerouslySetInnerHTML={{ __html: String(school.schoolAddress).replace(/<br\s*\/?>/gi, '<br/>') }}
              />
            )}
            {school.schoolEmail && <p className="admit-school-addr">{school.schoolEmail}</p>}
          </div>
        </div>

        {/* Admit card title + exam name */}
        <div className="admit-title-block">
          <span className="admit-title">ADMIT CARD</span>
          <span className="admit-exam">{examName ? `Exam: ${examName}` : 'Exam: ______________________'}</span>
        </div>

        {/* Student fields */}
        <div className="admit-fields">
          <div className="admit-field admit-field-wide">
            <span className="admit-label">Name</span>
            <span className="admit-value">{s.Student_Name || ''}</span>
          </div>
          <div className="admit-field admit-field-wide">
            <span className="admit-label">Father's Name</span>
            <span className="admit-value">{s.Student_Father || ''}</span>
          </div>
          <div className="admit-field">
            <span className="admit-label">Class</span>
            <span className="admit-value">{s.Student_Class || ''}</span>
          </div>
          <div className="admit-field">
            <span className="admit-label">Section</span>
            <span className="admit-value">{s.Student_Section || ''}</span>
          </div>
          <div className="admit-field">
            <span className="admit-label">Serial No.</span>
            <span className="admit-value">{s.Serial_No || ''}</span>
          </div>
          <div className="admit-field">
            <span className="admit-label">Admission No.</span>
            <span className="admit-value">{s.Admission_Number || ''}</span>
          </div>
        </div>

        {/* Signatures */}
        <div className="admit-sign-row">
          {SIG_ROLES.map(role => (
            <div key={role} className="admit-sign">
              <div className="admit-sign-imgbox">
                {sigs?.[role]?.url && (
                  <img src={sigs[role].url} style={sigImgStyle(sigs[role])} alt="" />
                )}
              </div>
              <div className="admit-sign-line" />
              <span>{SIG_LABELS[role]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdmitCardPage = () => {
  const { schoolId } = useParams();
  const [schoolInfo, setSchoolInfo] = useState({});
  const [examName, setExamName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [alert, setAlert] = useState(null);
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classLoading, setClassLoading] = useState(false);
  const [sigs, setSigs] = useState({ classTeacher: null, examIC: null, principal: null });
  const [sigUploading, setSigUploading] = useState('');
  // { role, img, src, imgW, imgH, dispW, dispH, sel: {x,y,w,h} } — sel in display px
  const [crop, setCrop] = useState(null);
  const dragRef = useRef(null);
  const holdRef = useRef(null);
  const cropViewRef = useRef(null);
  const layoutTimer = useRef(null);
  const layoutDirty = useRef({});
  const printRef = useRef(null);

  const token = getToken(schoolId);
  const headers = { Authorization: `Bearer ${token}` };

  // Load school info for the header
  useEffect(() => {
    fetch(`${API_URL}/school/info`, { headers })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          const info = d.data || {};
          if (info.schoolLogo) info.schoolLogo = buildBlobUrl(info.schoolLogo);
          setSchoolInfo(info);
        }
      })
      .catch(() => {});
    fetch(`${API_URL}/results/class-options`, { headers })
      .then(r => r.json())
      .then(d => {
        if (d.success) setClassOptions(d.data?.options || []);
      })
      .catch(() => {});
    fetch(`${API_URL}/school/admit-signatures`, { headers })
      .then(r => r.json())
      .then(d => {
        if (d.success) setSigs(d.data || {});
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Crop selection drag/resize — mode is 'move' or a corner ('nw','ne','sw','se')
  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    setCrop(c => {
      if (!c) return c;
      let { x, y, w, h } = d.sel;
      const MIN = 24;
      if (d.mode === 'move') {
        x = clamp(x + dx, 0, c.dispW - w);
        y = clamp(y + dy, 0, c.dispH - h);
      } else {
        if (d.mode.includes('w')) { const nx = clamp(x + dx, 0, x + w - MIN); w += x - nx; x = nx; }
        if (d.mode.includes('e')) { w = clamp(w + dx, MIN, c.dispW - x); }
        if (d.mode.includes('n')) { const ny = clamp(y + dy, 0, y + h - MIN); h += y - ny; y = ny; }
        if (d.mode.includes('s')) { h = clamp(h + dy, MIN, c.dispH - y); }
      }
      return { ...c, sel: { x, y, w, h } };
    });
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', endDrag);
  }, [onDragMove]);

  const startDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setCrop(c => {
      dragRef.current = { mode, sx: e.clientX, sy: e.clientY, sel: { ...c.sel } };
      return c;
    });
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag);
  };

  // Zoom the whole editor — selection scales with it so the same area stays selected
  const zoomCrop = (factor) => {
    setCrop(c => {
      if (!c) return c;
      const f = clamp(c.dispW * factor, 120, 2000) / c.dispW;
      if (f === 1) return c;
      return {
        ...c,
        dispW: Math.round(c.dispW * f),
        dispH: Math.round(c.dispH * f),
        sel: { x: c.sel.x * f, y: c.sel.y * f, w: c.sel.w * f, h: c.sel.h * f },
      };
    });
  };

  const nudgeSel = (dx, dy) => {
    setCrop(c => {
      if (!c) return c;
      const x = clamp(c.sel.x + dx, 0, c.dispW - c.sel.w);
      const y = clamp(c.sel.y + dy, 0, c.dispH - c.sel.h);
      if (x === c.sel.x && y === c.sel.y) return c;
      return { ...c, sel: { ...c.sel, x, y } };
    });
  };

  // Hold-to-repeat for the zoom / arrow buttons
  const startHold = (fn) => {
    fn();
    stopHold();
    holdRef.current = setInterval(fn, 90);
  };
  const stopHold = () => {
    if (holdRef.current) {
      clearInterval(holdRef.current);
      holdRef.current = null;
    }
  };
  useEffect(() => stopHold, []);

  // Keep the selection box visible inside the scrollable viewport
  useEffect(() => {
    const vp = cropViewRef.current;
    if (!vp || !crop) return;
    const cx = crop.sel.x + crop.sel.w / 2;
    const cy = crop.sel.y + crop.sel.h / 2;
    if (crop.dispW > vp.clientWidth) vp.scrollLeft = clamp(cx - vp.clientWidth / 2, 0, crop.dispW - vp.clientWidth);
    if (crop.dispH > vp.clientHeight) vp.scrollTop = clamp(cy - vp.clientHeight / 2, 0, crop.dispH - vp.clientHeight);
  }, [crop]);

  const openCropEditor = (role, src) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(380 / img.width, 300 / img.height);
      const dispW = Math.max(1, Math.round(img.width * scale));
      const dispH = Math.max(1, Math.round(img.height * scale));
      setCrop({
        role,
        img,
        src,
        imgW: img.width,
        imgH: img.height,
        dispW,
        dispH,
        // start inset so there is room to move/resize right away
        sel: {
          x: Math.round(dispW * 0.1),
          y: Math.round(dispH * 0.1),
          w: Math.round(dispW * 0.8),
          h: Math.round(dispH * 0.8),
        },
      });
    };
    img.src = src;
  };

  const handleSigFile = (role, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAlert({ type: 'error', msg: 'Please choose an image file' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAlert({ type: 'error', msg: 'Image must be under 5MB' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => openCropEditor(role, reader.result);
    reader.readAsDataURL(file);
  };

  // Re-open the saved signature in the crop editor (zoom / move / re-crop)
  const adjustSig = async (role) => {
    setSigUploading(role);
    try {
      const res = await fetch(`${API_URL}/school/admit-signatures/${role}/data`, { headers });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not load saved signature');
      openCropEditor(role, data.data.dataUrl);
    } catch (err) {
      setAlert({ type: 'error', msg: err.message || 'Failed to load signature for adjusting' });
    } finally {
      setSigUploading('');
    }
  };

  // Move / resize the signature on the card itself; persisted after a short pause
  const saveLayouts = async () => {
    const dirty = layoutDirty.current;
    layoutDirty.current = {};
    for (const [role, layout] of Object.entries(dirty)) {
      try {
        await fetch(`${API_URL}/school/admit-signatures`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ role, layout }),
        });
      } catch {
        // position stays applied locally; retried on next change
      }
    }
  };

  const updateSigLayout = (role, dx, dy, dh) => {
    setSigs(prev => {
      const cur = prev[role];
      if (!cur) return prev;
      const l = { ...DEFAULT_SIG_LAYOUT, ...(cur.layout || {}) };
      const layout = dx === null
        ? { ...DEFAULT_SIG_LAYOUT }
        : {
            x: clamp(l.x + dx, -120, 120),
            y: clamp(l.y + dy, -80, 40),
            h: clamp(l.h + dh, 16, 120),
          };
      layoutDirty.current[role] = layout;
      if (layoutTimer.current) clearTimeout(layoutTimer.current);
      layoutTimer.current = setTimeout(saveLayouts, 800);
      return { ...prev, [role]: { ...cur, layout } };
    });
  };

  const saveCrop = async () => {
    if (!crop) return;
    // map display-space selection back to natural image pixels
    const scaleX = crop.imgW / crop.dispW;
    const scaleY = crop.imgH / crop.dispH;
    const sx = crop.sel.x * scaleX;
    const sy = crop.sel.y * scaleY;
    const sw = Math.max(1, crop.sel.w * scaleX);
    const sh = Math.max(1, crop.sel.h * scaleY);

    const out = document.createElement('canvas');
    const outScale = Math.min(1, 1200 / sw); // cap width to keep uploads small
    out.width = Math.round(sw * outScale);
    out.height = Math.round(sh * outScale);
    out.getContext('2d').drawImage(crop.img, sx, sy, sw, sh, 0, 0, out.width, out.height);
    const dataUrl = out.toDataURL('image/png');
    const role = crop.role;
    setCrop(null);
    setSigUploading(role);
    try {
      const res = await fetch(`${API_URL}/school/admit-signatures`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role,
          imageBase64: dataUrl,
          imageName: `${role}_signature.png`,
          contentType: 'image/png',
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Upload failed');
      setSigs(prev => ({ ...prev, [role]: data.data.signature }));
      setAlert({ type: 'success', msg: `${SIG_LABELS[role] || role} signature saved` });
    } catch (err) {
      setAlert({ type: 'error', msg: err.message || 'Signature upload failed' });
    } finally {
      setSigUploading('');
    }
  };

  const removeSig = async (role) => {
    setSigUploading(role);
    try {
      const res = await fetch(`${API_URL}/school/admit-signatures`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, remove: true }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Remove failed');
      setSigs(prev => ({ ...prev, [role]: null }));
    } catch (err) {
      setAlert({ type: 'error', msg: err.message || 'Failed to remove signature' });
    } finally {
      setSigUploading('');
    }
  };

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/students/search?q=${encodeURIComponent(searchQuery.trim())}`, { headers });
      const data = await res.json();
      if (data.success) setSearchResults(data.data || []);
    } catch {
      // ignore
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const addStudent = (student) => {
    setSearchResults([]);
    setSearchQuery('');
    if (entries.some(e => e.Student_Id === student.Student_Id)) {
      setAlert({ type: 'info', msg: 'Student already added' });
      return;
    }
    setEntries(prev => [
      ...prev,
      {
        Student_Id: student.Student_Id,
        Student_Name: student.Student_Name || '',
        Student_Father: student.Student_Father || '',
        Student_Class: student.Student_Class || '',
        Student_Section: student.Student_Section || '',
        Admission_Number: student.Admission_Number || '',
        Serial_No: student.Serial != null ? String(student.Serial) : '',
      },
    ]);
  };

  const updateEntry = (index, key, value) => {
    setEntries(prev => prev.map((e, i) => (i === index ? { ...e, [key]: value } : e)));
  };

  const removeEntry = (index) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => setEntries([]);

  const toggleClass = (label) => {
    setSelectedClasses(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  const toggleAllClasses = () => {
    setSelectedClasses(prev => (prev.length === classOptions.length ? [] : [...classOptions]));
  };

  // Add every student of the selected class-sections in one go
  const addClasses = async () => {
    if (!selectedClasses.length) return;
    setClassLoading(true);
    try {
      const fetchOne = async (label) => {
        // label is "CLASS" or "CLASS-SECTION" — section is after the last dash
        const dash = label.lastIndexOf('-');
        const className = dash > 0 ? label.slice(0, dash) : label;
        const section = dash > 0 ? label.slice(dash + 1) : '';
        const params = new URLSearchParams({ className });
        if (section) params.set('section', section);
        const res = await fetch(`${API_URL}/results/students?${params}`, { headers });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || `Failed to load ${label}`);
        return data.data || [];
      };

      const lists = await Promise.all(selectedClasses.map(fetchOne));
      const students = lists
        .flat()
        .filter(s => (s.Status || '').toLowerCase() !== 'deleted')
        .sort((a, b) =>
          (a.Student_Class || '').localeCompare(b.Student_Class || '') ||
          (a.Student_Section || '').localeCompare(b.Student_Section || '') ||
          (a.Student_Name || '').localeCompare(b.Student_Name || ''));

      setEntries(prev => {
        const existing = new Set(prev.map(e => e.Student_Id));
        const added = [];
        for (const s of students) {
          if (existing.has(s.Student_Id)) continue;
          existing.add(s.Student_Id);
          added.push({
            Student_Id: s.Student_Id,
            Student_Name: s.Student_Name || '',
            Student_Father: s.Student_Father || '',
            Student_Class: s.Student_Class || '',
            Student_Section: s.Student_Section || '',
            Admission_Number: s.Admission_Number || '',
            Serial_No: s.Serial != null ? String(s.Serial) : '',
          });
        }
        setAlert({
          type: added.length ? 'success' : 'info',
          msg: added.length
            ? `Added ${added.length} student${added.length === 1 ? '' : 's'} from ${selectedClasses.join(', ')}`
            : 'No new students to add',
        });
        return [...prev, ...added];
      });
      setSelectedClasses([]);
    } catch (err) {
      setAlert({ type: 'error', msg: err.message || 'Failed to load class students' });
    } finally {
      setClassLoading(false);
    }
  };

  // Print — 2 half-page cards per A4 sheet, stacked top & bottom.
  // A single card prints on the top half, leaving the bottom half empty.
  const handlePrint = () => {
    if (!printRef.current || entries.length === 0) {
      setAlert({ type: 'error', msg: 'Add at least one student to print' });
      return;
    }

    const cards = Array.from(printRef.current.querySelectorAll('.admit-card'));
    let pagesHtml = '';
    for (let i = 0; i < cards.length; i += 2) {
      const top = cards[i] ? cards[i].outerHTML : '';
      const bottom = cards[i + 1] ? cards[i + 1].outerHTML : '<div class="admit-card admit-card-empty"></div>';
      pagesHtml += `<div class="admit-sheet">${top}${bottom}</div>`;
    }

    const printStyles = `
      <style>
        @page { size: A4 portrait; margin: 0; }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          box-sizing: border-box;
        }
        html, body {
          margin: 0; padding: 0;
          background: #fff !important;
          color: #000;
          font-family: 'Times New Roman', Times, serif;
        }
        .admit-sheet {
          width: 210mm;
          height: 297mm;
          display: flex;
          flex-direction: column;
          page-break-after: always;
          page-break-inside: avoid;
        }
        .admit-sheet:last-child { page-break-after: auto; }
        .admit-card {
          width: 210mm;
          height: 148.5mm;
          padding: 8mm 10mm;
          border-bottom: 1px dashed #999;
          overflow: hidden;
        }
        .admit-card-empty { border-bottom: none; }
        .admit-card-inner {
          height: 100%;
          border: 1.5px solid #333;
          padding: 6mm 8mm;
          display: flex;
          flex-direction: column;
        }
        .admit-header {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: center;
          text-align: center;
          padding-bottom: 6px;
          border-bottom: 1.5px solid #333;
          margin-bottom: 6px;
        }
        .admit-logo { width: 84px; height: 84px; object-fit: contain; }
        .admit-header-text { text-align: center; }
        .admit-school-name {
          margin: 0;
          font-size: 32px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #000;
        }
        .admit-school-addr { margin: 1px 0; font-size: 16px; color: #333; }
        .admit-title-block {
          text-align: center;
          margin: 6px 0 12px;
        }
        .admit-title {
          display: inline-block;
          font-size: 26px;
          font-weight: 700;
          letter-spacing: 3px;
          border: 1.5px solid #333;
          padding: 4px 24px;
          border-radius: 3px;
        }
        .admit-exam {
          display: block;
          margin-top: 8px;
          font-size: 21px;
          font-weight: 600;
        }
        .admit-fields {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px 28px;
          margin-top: 6px;
          flex: 1;
          align-content: start;
        }
        .admit-field {
          display: flex;
          align-items: baseline;
          gap: 8px;
          font-size: 21px;
        }
        .admit-field-wide { grid-column: 1 / -1; }
        .admit-label { font-weight: 600; min-width: 165px; }
        .admit-value {
          flex: 1;
          border-bottom: 1px solid #333;
          min-height: 28px;
          padding: 0 5px 2px;
        }
        .admit-sign-row {
          display: flex;
          justify-content: space-between;
          margin-top: auto;
          padding-top: 16px;
        }
        .admit-sign { text-align: center; font-size: 17px; }
        .admit-sign-imgbox {
          height: 50px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          margin-bottom: 2px;
          overflow: visible;
        }
        .admit-sign-imgbox img {
          max-width: 165px;
          object-fit: contain;
        }
        .admit-sign-line {
          width: 190px;
          border-top: 1px solid #333;
          margin-bottom: 4px;
        }
      </style>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><meta charset="utf-8">${printStyles}</head><body>${pagesHtml}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    // wait for signature/logo images before printing
    const imgs = Array.from(printWindow.document.images || []);
    Promise.all(imgs.map(im => (im.complete ? Promise.resolve() : new Promise(r => { im.onload = im.onerror = r; }))))
      .then(() => setTimeout(() => { printWindow.print(); printWindow.close(); }, 300));
  };

  return (
    <div className="admit-page">
      <header className="dash-topbar">
        <div>
          <h1 className="page-title">Admit Card</h1>
          <p className="page-subtitle">Set exam name, add students, and print 2 cards per A4 sheet</p>
        </div>
        <button className="docgen-btn docgen-btn-primary" onClick={handlePrint} disabled={entries.length === 0}>
          Print {entries.length > 0 && `${entries.length} card${entries.length === 1 ? '' : 's'} (${Math.ceil(entries.length / 2)} sheet${Math.ceil(entries.length / 2) === 1 ? '' : 's'})`}
        </button>
      </header>

      {alert && (
        <div className={`docgen-alert docgen-alert-${alert.type}`}>
          <span>{alert.msg}</span>
          <button onClick={() => setAlert(null)}>&times;</button>
        </div>
      )}

      <div className="admit-layout">
        {/* Left: controls */}
        <div className="admit-controls">
          <div className="docgen-section">
            <h3 className="docgen-section-title">Exam Name</h3>
            <input
              type="text"
              className="docgen-input"
              placeholder="e.g. Half-Yearly Examination 2026 (optional)"
              value={examName}
              onChange={e => setExamName(e.target.value)}
            />
            <p className="admit-hint">Applied to all admit cards. Leave blank to print an empty exam line.</p>
          </div>

          <div className="docgen-section">
            <h3 className="docgen-section-title">Signature Images</h3>
            {SIG_ROLES.map(role => (
              <div key={role} className="admit-sig-block">
                <div className="admit-sig-row">
                  <span className="admit-sig-role">{SIG_LABELS[role]}</span>
                  <div className="admit-sig-preview">
                    {sigs?.[role]?.url ? <img src={sigs[role].url} alt="" /> : <span>None</span>}
                  </div>
                  <label className="docgen-btn-sm admit-sig-upload">
                    {sigUploading === role ? 'Saving...' : (sigs?.[role] ? 'Replace' : 'Upload')}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={!!sigUploading}
                      onChange={e => { handleSigFile(role, e.target.files[0]); e.target.value = ''; }}
                    />
                  </label>
                  {sigs?.[role] && (
                    <>
                      <button className="docgen-btn-sm" disabled={!!sigUploading} onClick={() => adjustSig(role)}>
                        Re-crop
                      </button>
                      <button className="docgen-btn-sm admit-remove" disabled={!!sigUploading} onClick={() => removeSig(role)}>
                        Remove
                      </button>
                    </>
                  )}
                </div>
                {sigs?.[role] && (
                  <div className="admit-sig-position">
                    <span className="admit-sig-pos-label">Position on card</span>
                    <button className="docgen-btn-sm" title="Move up"
                      onPointerDown={() => startHold(() => updateSigLayout(role, 0, -2, 0))}
                      onPointerUp={stopHold} onPointerLeave={stopHold}>↑</button>
                    <button className="docgen-btn-sm" title="Move down"
                      onPointerDown={() => startHold(() => updateSigLayout(role, 0, 2, 0))}
                      onPointerUp={stopHold} onPointerLeave={stopHold}>↓</button>
                    <button className="docgen-btn-sm" title="Move left"
                      onPointerDown={() => startHold(() => updateSigLayout(role, -2, 0, 0))}
                      onPointerUp={stopHold} onPointerLeave={stopHold}>←</button>
                    <button className="docgen-btn-sm" title="Move right"
                      onPointerDown={() => startHold(() => updateSigLayout(role, 2, 0, 0))}
                      onPointerUp={stopHold} onPointerLeave={stopHold}>→</button>
                    <button className="docgen-btn-sm" title="Smaller"
                      onPointerDown={() => startHold(() => updateSigLayout(role, 0, 0, -2))}
                      onPointerUp={stopHold} onPointerLeave={stopHold}>−</button>
                    <button className="docgen-btn-sm" title="Bigger"
                      onPointerDown={() => startHold(() => updateSigLayout(role, 0, 0, 2))}
                      onPointerUp={stopHold} onPointerLeave={stopHold}>+</button>
                    <button className="docgen-btn-sm" title="Reset position" onClick={() => updateSigLayout(role, null)}>
                      Reset
                    </button>
                  </div>
                )}
              </div>
            ))}
            <p className="admit-hint">Printed above the Class Teacher / Exam IC / Principal line. Use the arrows / − + to move and size the signature on the card — watch the preview on the right.</p>
          </div>

          <div className="docgen-section">
            <div className="admit-list-head">
              <h3 className="docgen-section-title" style={{ margin: 0 }}>Add Classes</h3>
              {classOptions.length > 0 && (
                <button className="docgen-btn-sm" onClick={toggleAllClasses}>
                  {selectedClasses.length === classOptions.length ? 'Unselect all' : 'Select all'}
                </button>
              )}
            </div>
            {classOptions.length === 0 ? (
              <p className="admit-hint">No classes found.</p>
            ) : (
              <div className="admit-class-grid">
                {classOptions.map(opt => (
                  <label key={opt} className={`admit-class-chip ${selectedClasses.includes(opt) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedClasses.includes(opt)}
                      onChange={() => toggleClass(opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            )}
            <div className="docgen-actions">
              <button
                className="docgen-btn docgen-btn-primary"
                onClick={addClasses}
                disabled={!selectedClasses.length || classLoading}
              >
                {classLoading
                  ? 'Adding...'
                  : `Add ${selectedClasses.length || ''} class${selectedClasses.length === 1 ? '' : 'es'}`}
              </button>
            </div>
            <p className="admit-hint">Pick one, several, or all class-sections — every student in them is added for bulk printing.</p>
          </div>

          <div className="docgen-section">
            <h3 className="docgen-section-title">Add Students</h3>
            <div className="docgen-search-row">
              <input
                type="text"
                className="docgen-input"
                placeholder="Search by name, ID, or admission no..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
              <button className="docgen-btn docgen-btn-primary" onClick={handleSearch} disabled={searchLoading}>
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="docgen-search-results">
                {searchResults.map(s => (
                  <div key={s.Student_Id} className="docgen-search-item" onClick={() => addStudent(s)}>
                    <div className="docgen-search-info">
                      <strong>{s.Student_Name}</strong>
                      <span>{s.Student_Id} | Class {s.Student_Class}-{s.Student_Section}</span>
                    </div>
                    <span className="docgen-search-adm">{s.Admission_Number || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: preview */}
        <div className="admit-preview-panel">
          <h3 className="docgen-section-title">Preview (half page each)</h3>
          <div className="admit-preview-scroll" ref={printRef}>
            {entries.length === 0 ? (
              <AdmitCard school={schoolInfo} examName={examName} entry={{}} sigs={sigs} />
            ) : (
              entries.map(e => (
                <AdmitCard key={e.Student_Id} school={schoolInfo} examName={examName} entry={e} sigs={sigs} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Full-width: added students grid */}
      <div className="docgen-section admit-students-section">
        <div className="admit-list-head">
          <h3 className="docgen-section-title" style={{ margin: 0 }}>
            Added Students ({entries.length})
          </h3>
          <div className="admit-list-actions">
            {entries.length > 0 && (
              <button className="docgen-btn-sm" onClick={clearAll}>Clear all</button>
            )}
            <button className="docgen-btn docgen-btn-primary" onClick={handlePrint} disabled={entries.length === 0}>
              Print ({Math.ceil(entries.length / 2) || 0} sheet{Math.ceil(entries.length / 2) === 1 ? '' : 's'})
            </button>
          </div>
        </div>

        {entries.length === 0 ? (
          <p className="admit-hint">No students added yet.</p>
        ) : (
          <div className="admit-entry-list">
            {entries.map((e, i) => (
              <div key={e.Student_Id} className="admit-entry">
                <div className="admit-entry-main">
                  <strong>{e.Student_Name}</strong>
                  <span>{e.Student_Id} | Class {e.Student_Class}-{e.Student_Section}</span>
                </div>
                <div className="admit-entry-serial">
                  <label>Serial</label>
                  <input
                    type="text"
                    className="docgen-input"
                    value={e.Serial_No}
                    onChange={ev => updateEntry(i, 'Serial_No', ev.target.value)}
                  />
                </div>
                <button className="docgen-btn-sm admit-remove" onClick={() => removeEntry(i)}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Crop modal */}
      {crop && (
        <div className="admit-crop-overlay">
          <div className="admit-crop-modal">
            <h3 className="docgen-section-title">
              Crop {SIG_LABELS[crop.role] || crop.role} Signature
            </h3>
            <p className="admit-hint" style={{ margin: '0 0 10px' }}>
              Drag the box to move it, pull the corners to resize. Only the selected area is uploaded.
            </p>
            <div className="admit-crop-viewport" ref={cropViewRef}>
              <div className="admit-crop-stage" style={{ width: crop.dispW, height: crop.dispH }}>
                <img src={crop.src} alt="" draggable={false} />
                <div
                  className="admit-crop-sel"
                  style={{ left: crop.sel.x, top: crop.sel.y, width: crop.sel.w, height: crop.sel.h }}
                  onPointerDown={e => startDrag(e, 'move')}
                >
                  <span className="admit-crop-handle nw" onPointerDown={e => startDrag(e, 'nw')} />
                  <span className="admit-crop-handle ne" onPointerDown={e => startDrag(e, 'ne')} />
                  <span className="admit-crop-handle sw" onPointerDown={e => startDrag(e, 'sw')} />
                  <span className="admit-crop-handle se" onPointerDown={e => startDrag(e, 'se')} />
                </div>
              </div>
            </div>
            <div className="admit-crop-toolbar">
              <button className="docgen-btn-sm" title="Zoom out"
                onPointerDown={() => startHold(() => zoomCrop(1 / 1.15))}
                onPointerUp={stopHold} onPointerLeave={stopHold}>−</button>
              <button className="docgen-btn-sm" title="Zoom in"
                onPointerDown={() => startHold(() => zoomCrop(1.15))}
                onPointerUp={stopHold} onPointerLeave={stopHold}>+</button>
              <span className="admit-crop-toolbar-sep" />
              <button className="docgen-btn-sm" title="Move box up"
                onPointerDown={() => startHold(() => nudgeSel(0, -6))}
                onPointerUp={stopHold} onPointerLeave={stopHold}>↑</button>
              <button className="docgen-btn-sm" title="Move box down"
                onPointerDown={() => startHold(() => nudgeSel(0, 6))}
                onPointerUp={stopHold} onPointerLeave={stopHold}>↓</button>
              <button className="docgen-btn-sm" title="Move box left"
                onPointerDown={() => startHold(() => nudgeSel(-6, 0))}
                onPointerUp={stopHold} onPointerLeave={stopHold}>←</button>
              <button className="docgen-btn-sm" title="Move box right"
                onPointerDown={() => startHold(() => nudgeSel(6, 0))}
                onPointerUp={stopHold} onPointerLeave={stopHold}>→</button>
            </div>
            <div className="admit-crop-actions">
              <button
                className="docgen-btn-sm"
                onClick={() => setCrop(c => ({ ...c, sel: { x: 0, y: 0, w: c.dispW, h: c.dispH } }))}
              >
                Full image
              </button>
              <button className="docgen-btn-sm" onClick={() => setCrop(null)}>Cancel</button>
              <button className="docgen-btn docgen-btn-primary" onClick={saveCrop}>Crop &amp; Upload</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmitCardPage;
